import React, { useState, useRef, useEffect } from "react";
import { useQueryClient } from "@tanstack/react-query";
import DOMPurify from "dompurify";
import "../../index.css";
import { FaMicrophone } from "react-icons/fa";
import PropTypes from 'prop-types';

const ChatBubblev1 = ({
	userText,
	npcText,
	startRecording,
	stopRecording,
	onSendMessage,
	isProcessing,
	client
}) => {
	const [inputValue, setInputValue] = useState("");
	const [isRecording, setIsRecording] = useState(false);
	const [isPlaying, setIsPlaying] = useState(false);
	const [isProcessingMessage, setIsProcessingMessage] = useState(false);
	const [isWaitingForResponse, setIsWaitingForResponse] = useState(false);
	const inputRef = useRef(null);
	const queryClient = useQueryClient();

	const handleInputChange = (e) => {
		const newValue = e.target.value;
		setInputValue(newValue);
		// Clear userText when user starts typing manually
		if (userText && client?.setUserText) {
			client.setUserText("");
		}
	};

	const handleSendClick = async () => {
		if (!inputValue.trim() || isProcessingMessage || isWaitingForResponse) return;

		try {
			setIsProcessingMessage(true);
			setIsWaitingForResponse(true);
			await onSendMessage(inputValue);
			setInputValue("");
			if (inputRef.current) {
				inputRef.current.focus();
			}
		} catch (error) {
			console.error("Error sending message:", error);
		} finally {
			setIsProcessingMessage(false);
		}
	};

	const handleKeyPress = (e) => {
		if (e.key === "Enter" && !e.shiftKey) {
			e.preventDefault();
			handleSendClick();
		}
	};

	const handleVoiceClick = async () => {
		if (isRecording) {
			setIsRecording(false);
			if (stopRecording) {
				try {
					setIsProcessingMessage(true);
					setIsWaitingForResponse(true);
					await stopRecording();
					// Wait for the recording to be fully processed
					await new Promise(resolve => setTimeout(resolve, 1500));

					// Send the recorded text if available
					if (userText && userText.trim()) {
						await onSendMessage(userText);
						setInputValue("");
						if (client?.setUserText) {
							client.setUserText("");
						}
					}
				} catch (error) {
					console.error("Error stopping recording:", error);
				} finally {
					setIsProcessingMessage(false);
				}
			}
		} else {
			// Clear any existing input when starting new recording
			setInputValue("");
			if (client?.setUserText) {
				client.setUserText("");
			}

			setIsRecording(true);
			if (startRecording) {
				try {
					await startRecording();
				} catch (error) {
					console.error("Error starting recording:", error);
					setIsRecording(false);
				}
			}
		}
	};

	// Effect to handle audio playback state and message clearing
	useEffect(() => {
		if (npcText) {
			setIsPlaying(true);
			setIsWaitingForResponse(false);
			// Clear input value when AI responds
			setInputValue("");
			if (client?.setUserText) {
				client.setUserText("");
			}
		} else {
			setIsPlaying(false);
		}
	}, [npcText, client]);

	return (
		<div className="chat-bubble-container">
			<div className="chat-bubble">
				{/* <div className="chat-messages">
					{npcText && (
						<div className="message npc-message">
							<div className="message-content">
								{DOMPurify.sanitize(npcText)}
							</div>
						</div>
					)}
				</div> */}
				<div className="chat-input-container">
					<div className="input-wrapper">
						<textarea
							ref={inputRef}
							value={userText || inputValue}
							onChange={handleInputChange}
							onKeyPress={handleKeyPress}
							placeholder={isRecording ? "Recording..." : isWaitingForResponse ? "Waiting for response..." : "Type your message here..."}
							disabled={isProcessingMessage || isWaitingForResponse}
							style={{
								opacity: (isProcessingMessage || isWaitingForResponse) ? 0.7 : 1,
								cursor: (isProcessingMessage || isWaitingForResponse) ? 'not-allowed' : 'text',
								backgroundColor: isRecording ? 'rgba(255, 0, 0, 0.1)' : 'transparent',
								transition: 'all 0.3s ease'
							}}
						/>
					</div>
					<div className="button-container">
						<select
							className="language-select"
							onChange={(e) => {
								if (e.target.value === "https://ar-shj.beyin.ae") {
									window.location.href = e.target.value;
								} else if (e.target.value === "ar-SA") {
									window.location.href = "https://en-shj.beyin.ae";
								}
							}}
							disabled={isProcessingMessage || isWaitingForResponse}
						>
							<option value="https://en-shj.beyin.ae">EN</option>
							<option value="https://ar-shj.beyin.ae">AR</option>
						</select>
						<button
							className={`voice-button ${isRecording ? "recording" : ""}`}
							onClick={handleVoiceClick}
							disabled={isProcessingMessage || isWaitingForResponse}
							style={{
								opacity: (isProcessingMessage || isWaitingForResponse) ? 0.7 : 1,
								cursor: (isProcessingMessage || isWaitingForResponse) ? 'not-allowed' : 'pointer',
								transition: 'all 0.3s ease'
							}}
						>
							<svg
								viewBox="0 0 24 24"
								fill="none"
								xmlns="http://www.w3.org/2000/svg"
								className="voice-icon"
							>
								<path
									d="M12 14C13.6569 14 15 12.6569 15 11V5C15 3.34315 13.6569 2 12 2C10.3431 2 9 3.34315 9 5V11C9 12.6569 10.3431 14 12 14Z"
									stroke="currentColor"
									strokeWidth="2"
									strokeLinecap="round"
									strokeLinejoin="round"
								/>
								<path
									d="M19 11V12C19 15.866 15.866 19 12 19M12 19C8.13401 19 5 15.866 5 12V11M12 19V22M12 22H15M12 22H9"
									stroke="currentColor"
									strokeWidth="2"
									strokeLinecap="round"
									strokeLinejoin="round"
								/>
							</svg>
						</button>
						<button
							className="send-button"
							onClick={handleSendClick}
							disabled={isProcessingMessage || isWaitingForResponse || !inputValue.trim()}
							style={{
								opacity: (isProcessingMessage || isWaitingForResponse || !inputValue.trim()) ? 0.7 : 1,
								cursor: (isProcessingMessage || isWaitingForResponse || !inputValue.trim()) ? 'not-allowed' : 'pointer',
								transition: 'all 0.3s ease'
							}}
						>
							<svg
								viewBox="0 0 24 24"
								fill="none"
								xmlns="http://www.w3.org/2000/svg"
								className="send-icon"
							>
								<path
									d="M22 2L11 13"
									stroke="currentColor"
									strokeWidth="2"
									strokeLinecap="round"
									strokeLinejoin="round"
								/>
								<path
									d="M22 2L15 22L11 13L2 9L22 2Z"
									stroke="currentColor"
									strokeWidth="2"
									strokeLinecap="round"
									strokeLinejoin="round"
								/>
							</svg>
						</button>
					</div>
				</div>
			</div>
		</div>
	);
};

ChatBubblev1.propTypes = {
	userText: PropTypes.string,
	npcText: PropTypes.string,
	startRecording: PropTypes.func.isRequired,
	stopRecording: PropTypes.func.isRequired,
	onSendMessage: PropTypes.func.isRequired,
	isProcessing: PropTypes.bool,
	client: PropTypes.object
};

ChatBubblev1.defaultProps = {
	userText: "",
	npcText: "",
	isProcessing: false,
	client: null
};

export default React.memo(ChatBubblev1);
